import {getLayoutId} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {useDialogContext} from 'contexts/DialogContext';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {FloatingButton} from 'shared/components/Header/RoomPlanner/FloatingButton';
import {useAppSelector} from 'store/customer';
import styled from 'styled-components';

import 'driver.js/dist/driver.css';
import {driver, Driver} from 'driver.js';
import {VideoPlayer} from 'shared/components/VideoPlayer';
import {ROOM_PLANNER_VIDEOS} from 'shared/components/VideoPlayer/data';
import {VideoInfo} from 'shared/components/VideoPlayer/entity/VideoInfo';
import {NavigationGuide} from 'shared/components/Header/RoomPlanner/NavigationGuide';
import {useTutorialHelper} from 'shared/components/Header/helper/useTutorialHelper';
import {KeyboardShortcutsContent} from 'shared/components/Header/RoomPlanner/KeyboardShortcuts';

export const NAVIGATION_GUIDE_KEY = 'room_planner_navigation_guide';
export const KEYBOARD_TOOLTIP_KEY = 'room_planner_keyboard_controls_tooltip';
export const TUTORIAL_BUTTON_TOOLTIP_KEY =
    'room_planner_tutorial_button_tooltip';

export const TutorialButton = () => {
    const {show: dialogIsVisible, showDialog, hideDialog} = useDialogContext();
    const layoutID = useAppSelector(getLayoutId);
    const [tutorialVisible, setTutorialVisible] = useState(false);
    const {checkTutorialOption, markTutorialOption, setRoomPlannerTutorial} =
        useTutorialHelper();
    const [nextAction, setNextAction] = useState<null | 'TIP' | 'KEYBOARD'>(
        null
    );
    const driverRef = useRef<Driver | null>(null);
    const showTipAfterClose = useRef(false);

    const destroyDriver = () => {
        if (driverRef.current) {
            driverRef.current.destroy();
            driverRef.current = null;
        }
    };

    const displayTooltip = useCallback(() => {
        if (checkTutorialOption(KEYBOARD_TOOLTIP_KEY) && !dialogIsVisible) {
            destroyDriver();

            const driverObject = driver({
                popoverClass: 'driverjs-popover',
                showProgress: false,
                showButtons: ['close', 'next'],
                steps: [
                    {
                        element: '.keyboard-shortcuts-button',
                        popover: {
                            title: 'New',
                            description:
                                'Keyboard shortcuts now available! Click to see the options',
                        },
                    },
                ],

                onHighlighted: (element) => {
                    element.addEventListener(
                        'click',
                        () => {
                            destroyDriver();
                            showDialog({
                                title: 'Available Keyboard Shortcuts',
                                message: <KeyboardShortcutsContent />,
                                hideYesButton: true,
                                hideNoButton: true,
                            });
                        },
                        {once: true}
                    );
                },
                onDestroyed: () => {
                    setTutorialVisible(false);

                    void setRoomPlannerTutorial({
                        [KEYBOARD_TOOLTIP_KEY]: true,
                    });
                },
            });

            driverRef.current = driverObject;
            setTutorialVisible(true);
            driverObject.drive();
        }
    }, [checkTutorialOption, dialogIsVisible, showDialog]);

    const displayTutorialButtonTooltip = useCallback(() => {
        if (
            checkTutorialOption(TUTORIAL_BUTTON_TOOLTIP_KEY) &&
            !dialogIsVisible
        ) {
            destroyDriver();

            const driverObject = driver({
                popoverClass: 'driverjs-popover',
                showProgress: false,
                showButtons: ['close', 'next'],
                steps: [
                    {
                        element: '.tutorial-button',
                        popover: {
                            title: 'Tip',
                            description:
                                'Replay the navigation guide or watch the getting started videos anytime from here',
                        },
                    },
                ],
                onDestroyed: () => {
                    setTutorialVisible(false);

                    void setRoomPlannerTutorial({
                        [TUTORIAL_BUTTON_TOOLTIP_KEY]: true,
                    });
                    setNextAction('KEYBOARD');
                },
            });

            driverRef.current = driverObject;
            setTutorialVisible(true);
            driverObject.drive();
        }
    }, [checkTutorialOption, dialogIsVisible, displayTooltip]);

    const afterClose = () => {
        destroyDriver();
        setNextAction(showTipAfterClose.current ? 'TIP' : 'KEYBOARD');
        showTipAfterClose.current = false;
    };

    const markGuideSeen = () => {
        if (checkTutorialOption(NAVIGATION_GUIDE_KEY)) {
            markTutorialOption({[NAVIGATION_GUIDE_KEY]: true});
        }
    };

    const getWatchedVideos = () =>
        Object.fromEntries(
            ROOM_PLANNER_VIDEOS.map((video) => [
                video.watchedKey,
                !checkTutorialOption(video.watchedKey),
            ])
        );

    const handleVideoWatched = useCallback(
        (video: VideoInfo) => {
            markTutorialOption({[video.watchedKey]: true});
        },
        [markTutorialOption]
    );

    const closeDialog = useCallback(() => {
        hideDialog();
        afterClose();
    }, [hideDialog, afterClose]);

    const showHelp = () => {
        showDialog({
            title: '3D Room Planner help',
            message: (
                <VideoPlayer
                    videos={ROOM_PLANNER_VIDEOS}
                    watched={getWatchedVideos()}
                    onWatched={handleVideoWatched}
                    onPlayGuide={showNavigationGuide}
                    onClose={closeDialog}
                />
            ),
            maxWidth: 1000,
            hideFooter: true,
            hideYesButton: true,
            hideNoButton: true,
            closeButton: false,
            keyboard: true,
            className: 'custom-rounded',
            headerClass: 'd-none',
            onHide: afterClose,
        });
    };

    const handleGuideDismiss = useCallback(() => {
        markGuideSeen();
        closeDialog();
    }, [markGuideSeen, closeDialog]);

    const handleGuideWatchVideos = useCallback(() => {
        markGuideSeen();
        showHelp();
    }, [markGuideSeen, showHelp]);

    const showNavigationGuide = () => {
        showDialog({
            title: 'Find your way around',
            message: (
                <NavigationGuide
                    onDismiss={handleGuideDismiss}
                    onWatchVideos={handleGuideWatchVideos}
                />
            ),
            maxWidth: 488,
            hideFooter: true,
            hideYesButton: true,
            hideNoButton: true,
            closeButton: false,
            keyboard: false,
            className: 'custom-rounded',
            headerClass: 'd-none',
            onHide: undefined,
        });
    };

    useEffect(() => {
        if (!dialogIsVisible && nextAction) {
            if (nextAction === 'TIP') {
                displayTutorialButtonTooltip();
            } else if (nextAction === 'KEYBOARD') {
                displayTooltip();
            }

            setNextAction(null);
        }
    }, [dialogIsVisible, nextAction]);

    const handleTutorialClick = useCallback(() => {
        destroyDriver();
        showTipAfterClose.current = false;
        showHelp();
    }, [showHelp]);

    useEffect(() => {
        if (!layoutID) return;

        if (checkTutorialOption(NAVIGATION_GUIDE_KEY)) {
            showTipAfterClose.current = true;
            showNavigationGuide();
        } else if (checkTutorialOption(TUTORIAL_BUTTON_TOOLTIP_KEY)) {
            displayTutorialButtonTooltip();
        }
    }, [layoutID]);

    return (
        <Button
            $hasShadow={!tutorialVisible}
            className="tutorial-button"
            icon="tutorial.svg"
            onClick={handleTutorialClick}
            title="Help"
        />
    );
};

const Button = styled(FloatingButton)<{$hasShadow?: boolean}>`
    ${({$hasShadow = true}) => {
        if (!$hasShadow) {
            return `
                box-shadow: none !important;
                border: 1px solid gray;
            `;
        }
    }}
`;
